import { useRef, useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, useInView, useSpring } from 'framer-motion';
import { CUSTOMER_LOGOS, REGISTER_URL } from '../config/constants';
import {
  ArrowRight,
  ChevronDown,
  Play,
  Layers,
  FileText,
  FileX,
  Calculator,
  Brain,
  Sparkles,
  Users,
  Workflow,
  LineChart,
  Clock,
  TrendingUp,
  Shield,
  Zap,
  Target,
  CheckCircle2,
  BarChart3,
  PieChart,
  Award,
  AlertTriangle,
  FileSpreadsheet,
  Mail,
  GitBranch,
  Bell,
  Eye,
  Timer,
  MessageSquare
} from 'lucide-react';
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_825c5321-5d95-4d75-842e-1e4ffe27dc80/artifacts/nz2oqlak_image.png';

const clientLogos = [
  { name: 'Rockman', logo: '/1.jpg' },
  { name: 'Kaitaing', logo: '/2.jpg' },
  { name: 'MMT', logo: '/3.jpg' },
  { name: 'Abhijay', logo: '/4.jpg' },
  { name: 'Client5', logo: '/5.jpg' },
  { name: 'Client6', logo: '/6.jpg' },
  { name: 'Client7', logo: '/7.jpg' },
  { name: 'Client8', logo: '/8.jpg' },
  { name: 'Client9', logo: '/9.jpg' },
  { name: 'Client10', logo: '/10.jpg' },
  { name: 'Client11', logo: '/11.jpg' },
  { name: 'Client12', logo: '/12.jpg' },
  { name: 'Client13', logo: '/13.png' },
  { name: 'Client14', logo: '/14.jpg' },
  { name: 'Client15', logo: '/15.jpg' },
  { name: 'Client16', logo: '/16.jpg' },
];

// Pain points from PDF
const painPoints = [
  { icon: FileX, title: 'RFQ Chaos', description: 'Multiple sources, version confusion, unclear final revisions' },
  { icon: AlertTriangle, title: 'BOM Conflicts', description: 'Teams lose track of latest updates and amendments' },
  { icon: Timer, title: 'Manual Costing', description: 'Spreadsheets with zero traceability, error-prone processes' },
  { icon: Eye, title: 'No Visibility', description: 'Teams work on outdated data, delayed decisions' },
];

// Solutions from PDF
const solutions = [
  {
    icon: Layers,
    title: 'Centralized RFQ Workspace',
    description: 'Live RFQ status, SLA timers, escalations, and alerts in one unified platform.',
    stat: '100%',
    statLabel: 'Visibility'
  },
  {
    icon: Target,
    title: 'BOM Version Control',
    description: 'Multi-level BOM with versions, ECN handling, and one-click comparison.',
    stat: '50-70%',
    statLabel: 'Faster'
  },
  {
    icon: Calculator,
    title: 'Auto-Estimates',
    description: 'Auto costing from BOM + reusable norms libraries (MHR, labour, tooling, packing).',
    stat: '<2%',
    statLabel: 'Error Rate'
  },
  {
    icon: BarChart3,
    title: 'Instant Profitability',
    description: 'Business case insights with P&L Summary, EBITDA, IRR & Payback views.',
    stat: '24/7',
    statLabel: 'Real-time'
  },
];

// ROI Metrics from Executive Deck
const roiMetrics = [
  { value: '40-70%', label: 'Faster Quotation Turnaround', icon: Zap },
  { value: '50-60%', label: 'Less Rework', icon: Shield },
  { value: '90%+', label: 'Fewer Missed Deadlines', icon: Clock },
  { value: '4-6hrs', label: 'Saved Per RFQ', icon: Timer },
];

// AI Copilots from Executive Deck
const aiCapabilities = [
  { icon: FileText, title: 'RFQ Intake AI', description: 'Normalizes requests into structured line items, detects missing data' },
  { icon: Layers, title: 'Drawing-to-BOM', description: 'Builds reviewable BOM drafts directly from engineering drawings' },
  { icon: Calculator, title: 'Smart Costing', description: 'Runs instant what-if scenarios for cost drivers' },
  { icon: MessageSquare, title: 'Prompt Analytics', description: 'Generates reports and summaries in plain language' },
];

// Workflow steps
const workflowSteps = [
  { name: 'RFQ', icon: FileText },
  { name: 'BOM', icon: Layers },
  { name: 'Costing', icon: Calculator },
  { name: 'Quote', icon: Target },
  { name: 'APQP', icon: CheckCircle2 },
  { name: 'SOP', icon: Award },
];

// Stats
const stats = [
  { value: '50+', label: 'Manufacturing Partners' },
  { value: '70%', label: 'Time Reduction' },
  { value: '100%', label: 'Audit Trail' },
  { value: '30-60', label: 'Days to Go-Live' },
];

// Reveal Section Component
const RevealSection = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const useMouseParallax = (strength = 10) => {
  const [position, setPosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * strength;
      const y = (e.clientY / window.innerHeight - 0.5) * strength;
      setPosition({ x, y });
    };

    window.addEventListener('mousemove', handleMouseMove);

    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [strength]);

  return position;
};

// 2D Manufacturing Part Drawing SVG
const ManufacturingDrawing = ({ mouseX, mouseY }) => (
  <motion.svg
    viewBox="0 0 200 150"
    className="w-full h-full"
    style={{ x: mouseX * 0.5, y: mouseY * 0.5 }}
  >
    <defs>
      <linearGradient id="drawingGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#8B5CF6" stopOpacity="0.8" />
        <stop offset="100%" stopColor="#00D4FF" stopOpacity="0.6" />
      </linearGradient>
    </defs>

    {/* Main shaft body */}
    <motion.rect x="30" y="50" width="140" height="30" fill="none" stroke="url(#drawingGrad)" strokeWidth="1.5"
      initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1.5 }} />

    {/* Left flange */}
    <motion.rect x="20" y="40" width="20" height="50" fill="none" stroke="url(#drawingGrad)" strokeWidth="1.5"
      initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1.5, delay: 0.2 }} />

    {/* Right flange */}
    <motion.rect x="160" y="40" width="20" height="50" fill="none" stroke="url(#drawingGrad)" strokeWidth="1.5"
      initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1.5, delay: 0.3 }} />

    {/* Holes */}
    <motion.circle cx="30" cy="65" r="8" fill="none" stroke="url(#drawingGrad)" strokeWidth="1"
      initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ duration: 0.5, delay: 0.5 }} />
    <motion.circle cx="170" cy="65" r="8" fill="none" stroke="url(#drawingGrad)" strokeWidth="1"
      initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ duration: 0.5, delay: 0.6 }} />

    {/* Center hole */}
    <motion.circle cx="100" cy="65" r="12" fill="none" stroke="url(#drawingGrad)" strokeWidth="1" strokeDasharray="4 2"
      initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ duration: 0.5, delay: 0.7 }} />

    {/* Dimension lines */}
    <motion.line x1="20" y1="100" x2="180" y2="100" stroke="rgba(0, 212, 255, 0.4)" strokeWidth="0.5"
      initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 0.8, delay: 1 }} />
    <motion.line x1="20" y1="98" x2="20" y2="102" stroke="rgba(0, 212, 255, 0.4)" strokeWidth="0.5" />
    <motion.line x1="180" y1="98" x2="180" y2="102" stroke="rgba(0, 212, 255, 0.4)" strokeWidth="0.5" />

    <motion.text x="100" y="112" fill="rgba(255,255,255,0.5)" fontSize="8" textAnchor="middle"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }}>
      160mm
    </motion.text>

    {/* Vertical dimension */}
    <motion.line x1="195" y1="40" x2="195" y2="90" stroke="rgba(0, 212, 255, 0.4)" strokeWidth="0.5"
      initial={{ scaleY: 0 }} animate={{ scaleY: 1 }} transition={{ duration: 0.8, delay: 1.1 }} />
    <motion.text x="195" y="70" fill="rgba(255,255,255,0.5)" fontSize="6" textAnchor="start" transform="rotate(90 195 70)"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3 }}>
      50mm
    </motion.text>

    {/* Part callouts */}
    <motion.text x="30" y="35" fill="rgba(139, 92, 246, 0.8)" fontSize="6"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }}>
      A - Flange
    </motion.text>
    <motion.text x="90" y="35" fill="rgba(0, 212, 255, 0.8)" fontSize="6"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5 }}>
      B - Shaft Body
    </motion.text>
  </motion.svg>
);

// Business Card SVG
const BusinessCardVisual = () => (
  <motion.div
    className="relative w-full h-full"
    initial={{ rotateY: -15 }}
    animate={{ rotateY: [-15, 0, -15] }}
    transition={{ duration: 4, repeat: Infinity }}
    style={{ transformStyle: 'preserve-3d' }}
  >
    <div className="w-full h-full rounded-lg overflow-hidden" style={{
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
    }}>
      {/* Card content */}
      <div className="p-4 h-full flex flex-col justify-between">
        <div>
          <div className="w-8 h-8 rounded bg-gradient-to-br from-cyan-500/30 to-purple-500/30 mb-2 flex items-center justify-center">
            <span className="text-cyan-400 text-xs font-bold">JD</span>
          </div>
          <div className="text-white/90 font-semibold text-sm">John Davidson</div>
          <div className="text-cyan-400/80 text-xs">Procurement Head</div>
        </div>
        <div className="space-y-1">
          <div className="text-white/40 text-[8px]">john.d@autoparts.com</div>
          <div className="text-white/40 text-[8px]">+1 555 123 4567</div>
          <div className="text-white/50 text-[8px] font-medium">AutoParts Manufacturing Inc.</div>
        </div>
      </div>
    </div>
  </motion.div>
);

// Mini chart for reports
const MiniChart = ({ type }) => (
  <div className="w-full h-full flex items-end justify-center gap-1 p-2">
    {type === 'bar' ? (
      [60, 80, 45, 90, 70, 85].map((h, i) => (
        <motion.div
          key={i}
          className="flex-1 bg-gradient-to-t from-cyan-500/50 to-purple-500/30 rounded-t"
          initial={{ height: 0 }}
          animate={{ height: `${h}%` }}
          transition={{ delay: i * 0.1, duration: 0.5 }}
        />
      ))
    ) : (
      <div className="relative w-full h-full">
        <motion.div
          className="absolute inset-2 rounded-full border-4 border-cyan-500/50"
          style={{ borderRightColor: 'rgba(139, 92, 246, 0.5)', borderBottomColor: 'rgba(139, 92, 246, 0.3)' }}
          animate={{ rotate: 360 }}
          transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-cyan-400 text-xs font-bold">72%</span>
        </div>
      </div>
    )}
  </div>
);

// AI Transform Visualization
const AITransformVisualization = () => {
  const [phase, setPhase] = useState(0);
  const mouseParallax = useMouseParallax(15);
  const springX = useSpring(mouseParallax.x, { stiffness: 100, damping: 20 });
  const springY = useSpring(mouseParallax.y, { stiffness: 100, damping: 20 });

  useEffect(() => {
    const interval = setInterval(() => setPhase((p) => (p + 1) % 4), 2500);
    return () => clearInterval(interval);
  }, []);

  const bomRows = [
    { part: 'SHAFT-001', qty: '2', mat: 'SS304' },
    { part: 'FLANGE-A', qty: '2', mat: 'Steel' },
    { part: 'BUSH-005', qty: '4', mat: 'Bronze' },
    { part: 'BOLT-M8', qty: '8', mat: 'Steel' },
  ];

  return (
    <div className="relative w-full h-[550px] lg:h-[600px]" style={{ perspective: '1500px' }}>
      {/* Floating particles */}
      {[...Array(25)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            width: Math.random() * 4 + 2,
            height: Math.random() * 4 + 2,
            background: i % 2 === 0 ? 'rgba(0, 212, 255, 0.5)' : 'rgba(139, 92, 246, 0.5)',
          }}
          animate={{
            y: [-20, 20, -20],
            opacity: [0.3, 0.7, 0.3],
          }}
          transition={{ duration: Math.random() * 3 + 2, repeat: Infinity, delay: Math.random() * 2 }}
        />
      ))}

      <div className="absolute inset-0 flex items-center justify-center">
        <div className="relative w-full max-w-5xl flex items-center justify-between px-4 mt-[-220px]">

          {/* LEFT: 2D Manufacturing Drawing */}
          <motion.div
            className="w-[300px] lg:w-[340px]"
            style={{ x: springX, y: springY }}
            animate={{ scale: phase === 0 ? 1.02 : 1 }}
          >
            <motion.div
              className="rounded-2xl overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(139, 92, 246, 0.02))',
                border: '1px solid rgba(139, 92, 246, 0.3)',
                boxShadow: '0 25px 60px -15px rgba(139, 92, 246, 0.25)',
              }}
            >
              <div className="px-4 py-3 border-b border-purple-500/20 flex items-center gap-2">
                <div className="flex gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-500/80" />
                  <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/80" />
                  <div className="w-2.5 h-2.5 rounded-full bg-green-500/80" />
                </div>
                <span className="ml-2 text-purple-400/80 text-xs font-mono">shaft_assembly.dwg</span>
              </div>
              <div className="p-4 h-[200px]">
                <ManufacturingDrawing mouseX={mouseParallax.x} mouseY={mouseParallax.y} />
              </div>

              {/* Scanning overlay */}
              {phase === 1 && (
                <motion.div
                  className="absolute inset-0 flex items-center justify-center rounded-2xl"
                  style={{ background: 'rgba(0, 0, 0, 0.6)' }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <div className="text-center">
                    <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}>
                      <Brain className="w-10 h-10 text-cyan-400 mx-auto mb-2" />
                    </motion.div>
                    <span className="text-cyan-400 text-sm">AI Reading Drawing...</span>
                  </div>
                </motion.div>
              )}
            </motion.div>
            <div className="text-center mt-3">
              <span className="text-purple-400/80 text-sm font-medium">📐 2D Engineering Drawing</span>
            </div>
          </motion.div>

          {/* CENTER: AI Brain Processing */}
          <motion.div
            className="relative z-20"
            style={{ x: springX * 0.3, y: springY * 0.3 }}
          >
            <motion.div
              className="relative w-28 h-28 lg:w-36 lg:h-36"
              animate={{ scale: phase === 1 || phase === 2 ? [1, 1.1, 1] : 1 }}
              transition={{ duration: 0.8 }}
            >
              {/* Orbital rings */}
              <motion.div
                className="absolute inset-0 rounded-full border-2 border-dashed border-cyan-500/30"
                animate={{ rotate: 360 }}
                transition={{ duration: 12, repeat: Infinity, ease: 'linear' }}
              />
              <motion.div
                className="absolute inset-3 rounded-full border border-purple-500/40"
                animate={{ rotate: -360 }}
                transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
              />

              {/* Core */}
              <motion.div
                className="absolute inset-6 rounded-full flex items-center justify-center"
                style={{
                  background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.2), rgba(139, 92, 246, 0.2))',
                  border: '2px solid rgba(0, 212, 255, 0.5)',
                }}
                animate={{
                  boxShadow: ['0 0 30px rgba(0, 212, 255, 0.3)', '0 0 50px rgba(139, 92, 246, 0.4)', '0 0 30px rgba(0, 212, 255, 0.3)']
                }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Brain className="w-8 h-8 lg:w-10 lg:h-10 text-cyan-400" />
              </motion.div>

              {/* Connection lines */}
              {(phase === 1 || phase === 2) && (
                <>
                  <motion.div
                    className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-full w-16 h-0.5"
                    style={{ background: 'linear-gradient(to right, transparent, rgba(139, 92, 246, 0.8))' }}
                    initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 0.4 }}
                  />
                  <motion.div
                    className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-full w-16 h-0.5"
                    style={{ background: 'linear-gradient(to left, transparent, rgba(0, 212, 255, 0.8))' }}
                    initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 0.4 }}
                  />
                </>
              )}
            </motion.div>

            <div className="text-center mt-3">
              <span className="text-cyan-400 text-sm font-medium">
                {phase === 0 && '🔍 Input'}
                {phase === 1 && '🧠 Processing'}
                {phase === 2 && '✨ Extracting'}
                {phase === 3 && '✅ Complete'}
              </span>
            </div>
          </motion.div>

          {/* RIGHT: BOM Output */}
          <motion.div
            className="w-[300px] lg:w-[340px]"
            style={{ x: springX * -1, y: springY * -1 }}
            animate={{ opacity: phase >= 2 ? 1 : 0.3, scale: phase >= 3 ? 1.02 : 1 }}
          >
            <motion.div
              className="rounded-2xl overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.1), rgba(0, 212, 255, 0.02))',
                border: '1px solid rgba(0, 212, 255, 0.3)',
                boxShadow: phase >= 3 ? '0 25px 60px -15px rgba(0, 212, 255, 0.25)' : 'none',
              }}
            >
              <div className="px-4 py-3 border-b border-cyan-500/20 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Layers className="w-4 h-4 text-cyan-400" />
                  <span className="text-cyan-400/80 text-xs font-mono">BOM_output.xlsx</span>
                </div>
                {phase >= 3 && (
                  <motion.span
                    initial={{ scale: 0 }} animate={{ scale: 1 }}
                    className="px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 text-xs"
                  >
                    ✓ Ready
                  </motion.span>
                )}
              </div>

              <div className="p-4">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-white/40 border-b border-cyan-500/20">
                      <th className="text-left py-2">Part No.</th>
                      <th className="text-center py-2">Qty</th>
                      <th className="text-right py-2">Material</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bomRows.map((row, i) => (
                      <motion.tr
                        key={i}
                        className="border-b border-cyan-500/10"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: phase >= 2 ? 1 : 0, x: phase >= 2 ? 0 : 20 }}
                        transition={{ delay: i * 0.12 }}
                      >
                        <td className="py-2 text-cyan-400 font-mono">{row.part}</td>
                        <td className="py-2 text-center text-white/70">{row.qty}</td>
                        <td className="py-2 text-right text-white/50">{row.mat}</td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
            <div className="text-center mt-3">
              <span className="text-cyan-400/80 text-sm font-medium">📋 Bill of Materials</span>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Phase indicators */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className={`h-1.5 rounded-full transition-all duration-300 ${phase === i ? 'w-8 bg-cyan-400' : 'w-2 bg-white/20'}`} />
        ))}
      </div>
    </div>
  );
};

// Pain point card
const PainCard = ({ icon: Icon, title }) => (
  <motion.div
    whileHover={{ scale: 1.05, y: -4 }}
    className="p-4 rounded-xl text-center cursor-pointer"
    style={{
      background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.08), rgba(239, 68, 68, 0.02))',
      border: '1px solid rgba(239, 68, 68, 0.2)',
    }}
  >
    <Icon className="w-6 h-6 text-red-400/80 mx-auto mb-2" />
    <span className="text-white/60 text-xs">{title}</span>
  </motion.div>
);

// Value metric card
const ValueCard = ({ icon: Icon, value, label, color }) => (
  <motion.div
    whileHover={{ scale: 1.05 }}
    className="text-center p-6 rounded-2xl"
    style={{
      background: `linear-gradient(135deg, ${color === 'cyan' ? 'rgba(0, 212, 255, 0.08)' : 'rgba(139, 92, 246, 0.08)'}, transparent)`,
      border: `1px solid ${color === 'cyan' ? 'rgba(0, 212, 255, 0.15)' : 'rgba(139, 92, 246, 0.15)'}`,
    }}
  >
    <Icon className={`w-6 h-6 mx-auto mb-3 ${color === 'cyan' ? 'text-cyan-400/60' : 'text-purple-400/60'}`} />
    <div className={`text-4xl font-bold mb-1 ${color === 'cyan' ? 'text-cyan-400' : 'text-purple-400'}`}>{value}</div>
    <div className="text-white/40 text-sm">{label}</div>
  </motion.div>
);

// Reveal component
const Reveal = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export const HomePage = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 0.5], [1, 0.95]);

  const modules = [
    { icon: Users, label: 'CRM' },
    { icon: FileText, label: 'RFQ' },
    { icon: Layers, label: 'BOM' },
    { icon: Calculator, label: 'Costing' },
    { icon: Workflow, label: 'Workflow' },
    { icon: LineChart, label: 'Analytics' },
  ];

  const aiFeatures = [
    { icon: Layers, title: 'Drawing → BOM', desc: 'Auto-extract parts' },
    { icon: Users, title: 'Card → Lead', desc: 'Instant CRM entry' },
    { icon: FileText, title: 'Notes → Minutes', desc: 'AI structuring' },
    { icon: BarChart3, title: 'Ask → Report', desc: 'Plain language' },
  ];

  return (
    <div className="relative" data-testid="home-page">
      {/* ===== HERO: AI Drawing to BOM ===== */}
      <section ref={heroRef} className="relative min-h-screen overflow-hidden" data-testid="hero-section">
        <motion.div style={{ opacity: heroOpacity, scale: heroScale }} className="relative z-10 pt-24">
          <div className="max-w-7xl mx-auto px-6 text-center mb-6">
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-3 px-5 py-2 mb-4 rounded-full"
              style={{ background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.15), rgba(0, 212, 255, 0.15))', border: '1px solid rgba(139, 92, 246, 0.3)' }}
            >
              <motion.div animate={{ rotate: 360 }} transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}>
                <Brain className="w-5 h-5 text-purple-400" />
              </motion.div>
              <span className="text-white font-medium text-sm">AI-Powered Manufacturing Intelligence</span>
            </motion.div>

            {/* Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-5xl max-sm:text-2xl  font-bold leading-[1.05] mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              <span className="text-white">From Drawing to</span>{' '}
              <span className="text-gradient-cyan">BOM</span>
              <br />
              <span className="text-white/80">in Seconds</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-white/50 text-base max-w-2xl mx-auto mb-8"
            >
              AI reads your 2D engineering drawings and auto-generates structured Bill of Materials.
              From chaos to control-faster quotes, less rework, better margins.
            </motion.p>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="flex flex-wrap justify-center gap-4 mb-8"
            >
              {/* <Link
                to={REGISTER_URL}
                className="group px-6 py-2.5 bg-gradient-to-r from-purple-500 to-cyan-500 text-white font-semibold rounded-xl hover:shadow-[0_0_50px_rgba(139,92,246,0.4)] transition-all flex items-center gap-2"
                data-testid="hero-cta"
              >
                <span>Start Free Trial</span>
                <motion.div animate={{ x: [0, 4, 0] }} transition={{ duration: 1.5, repeat: Infinity }}>
                  <ArrowRight className="w-5 h-5" />
                </motion.div>
              </Link> */}
              {/* <Link to="/pricing" className="px-8 py-2.5 border border-white/20 text-white/80 rounded-xl hover:bg-white/5 transition-all flex items-center gap-2">
                <span>View Pricing</span>
                <ArrowRight className="w-4 h-4" />
              </Link> */}
            </motion.div>
          </div>

          {/* 3D Visualization */}
          <AITransformVisualization />
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2 }}
          className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 cursor-pointer"
          onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
        >
          <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 2, repeat: Infinity }}>
            <ChevronDown className="w-6 h-6 text-white/30" />
          </motion.div>
        </motion.div>
      </section>



      {/* Problem Section - "The Hidden Complexity" */}
      <section className="py-24 lg:py-32" data-testid="problem-section">
        <div className="max-w-7xl mx-auto px-6">
          <RevealSection className="text-center mb-16">
            <span className="text-red-400/80 text-sm tracking-widest uppercase mb-4 block">
              The Challenge
            </span>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-semibold mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              The Hidden Complexity
            </h2>
            <p className="text-white/50 max-w-2xl mx-auto">
              Between RFQ receipt and final quote, teams lose speed, ownership, and margin.
              Sound familiar?
            </p>
          </RevealSection>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            {[
              { icon: Mail, title: 'Email Chaos' },
              { icon: GitBranch, title: 'Version Drift' },
              { icon: FileSpreadsheet, title: 'Spreadsheet Hell' },
              { icon: Bell, title: 'Approval Delays' },
              { icon: Eye, title: 'Zero Visibility' },
            ].map((pain, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <PainCard icon={pain.icon} title={pain.title} />
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.5}>
            <div className="p-6 rounded-2xl text-center" style={{
              background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.05), transparent)',
              border: '1px solid rgba(239, 68, 68, 0.1)',
            }}>
              <AlertTriangle className="w-6 h-6 text-red-400/60 mx-auto mb-2" />
              <p className="text-white/60 text-sm">
                <span className="text-red-400 font-medium">Result:</span> Slower quotes, higher rework,
                hidden revision risk, weak profitability visibility
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===== STORY SECTION 2: The Solution ===== */}
      <section className="py-24" data-testid="solution-section">
        <div className="max-w-6xl mx-auto px-6">
          <Reveal className="text-center mb-12">
            <span className="text-cyan-400 text-sm font-medium tracking-wider uppercase">The Solution</span>
            <h2 className="text-4xl md:text-5xl font-bold mt-2 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              One Platform, <span className="text-gradient-cyan">Complete Control</span>
            </h2>
            <p className="text-white/50 max-w-2xl mx-auto">
              CRM, RFQ, BOM, Costing, Workflow, Analytics-all connected.
              From first customer contact to final business decision.
            </p>
          </Reveal>

          {/* Module flow */}
          <Reveal delay={0.2}>
            <div className="relative mb-16">
              <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-cyan-500/30 to-transparent hidden md:block" />
              <div className="flex flex-wrap justify-center gap-4">
                {modules.map((mod, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ delay: i * 0.1 }}
                    whileHover={{ scale: 1.1, y: -8, boxShadow: '0 0 30px rgba(0, 212, 255, 0.3)' }}
                    className="relative z-10 w-20 h-20 rounded-2xl flex flex-col items-center justify-center gap-1 cursor-pointer"
                    style={{
                      background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.1), rgba(139, 92, 246, 0.05))',
                      border: '1px solid rgba(0, 212, 255, 0.2)',
                    }}
                  >
                    <mod.icon className="w-6 h-6 text-cyan-400" />
                    <span className="text-white/70 text-xs">{mod.label}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </Reveal>

          {/* Value pillars */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: Zap, title: 'Speed', desc: 'Days not weeks' },
              { icon: Shield, title: 'Control', desc: 'Single truth' },
              { icon: Target, title: 'Discipline', desc: 'SLA timers' },
              { icon: Eye, title: 'Visibility', desc: 'Live dashboards' },
            ].map((v, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <motion.div
                  whileHover={{ scale: 1.03 }}
                  className="p-5 rounded-xl text-center"
                  style={{
                    background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.05), transparent)',
                    border: '1px solid rgba(0, 212, 255, 0.1)',
                  }}
                >
                  <v.icon className="w-5 h-5 text-cyan-400 mx-auto mb-2" />
                  <div className="text-white/90 font-semibold text-sm">{v.title}</div>
                  <div className="text-white/40 text-xs">{v.desc}</div>
                </motion.div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== STORY SECTION 3: AI Copilots ===== */}
      <section className="py-24 border-t border-white/5" data-testid="ai-section">
        <div className="max-w-5xl mx-auto px-6">
          <Reveal className="text-center mb-12">
            <motion.div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4"
              style={{ background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.15), rgba(0, 212, 255, 0.15))', border: '1px solid rgba(139, 92, 246, 0.3)' }}
              animate={{ boxShadow: ['0 0 20px rgba(139, 92, 246, 0.1)', '0 0 40px rgba(139, 92, 246, 0.2)', '0 0 20px rgba(139, 92, 246, 0.1)'] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <Sparkles className="w-4 h-4 text-purple-400" />
              <span className="text-purple-400 text-xs font-medium">AI Copilots</span>
            </motion.div>
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              AI That <span className="text-gradient-cyan">Actually Works</span>
            </h2>
            <p className="text-white/50 max-w-xl mx-auto">
              Built on your data backbone. Every output traceable, every assumption visible.
            </p>
          </Reveal>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {aiFeatures.map((feat, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <motion.div
                  whileHover={{ scale: 1.05, y: -5 }}
                  className="p-6 rounded-2xl text-center cursor-pointer"
                  style={{
                    background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.08), rgba(0, 212, 255, 0.04))',
                    border: '1px solid rgba(139, 92, 246, 0.15)',
                  }}
                >
                  <motion.div
                    className="w-12 h-12 mx-auto mb-3 rounded-xl flex items-center justify-center"
                    style={{ background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2), rgba(0, 212, 255, 0.1))', border: '1px solid rgba(139, 92, 246, 0.3)' }}
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 2, repeat: Infinity, delay: i * 0.3 }}
                  >
                    <feat.icon className="w-5 h-5 text-purple-400" />
                  </motion.div>
                  <h3 className="text-white/90 font-semibold text-sm mb-1">{feat.title}</h3>
                  <p className="text-white/40 text-xs">{feat.desc}</p>
                </motion.div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== STORY SECTION 4: ROI & Results ===== */}
      <section className="py-24" data-testid="roi-section">
        <div className="max-w-5xl mx-auto px-6">
          <Reveal className="text-center mb-12">
            <span className="text-green-400 text-sm font-medium tracking-wider uppercase">Proven Results</span>
            <h2 className="text-4xl md:text-5xl font-bold mt-2 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              ROI You Can <span className="text-gradient-cyan">Measure</span>
            </h2>
            <p className="text-white/50 max-w-xl mx-auto">
              Real results from manufacturing companies who made the switch
            </p>
          </Reveal>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            <Reveal><ValueCard icon={Zap} value=">70%" label="Faster Quotes" color="cyan" /></Reveal>
            <Reveal delay={0.1}><ValueCard icon={Target} value="<70%" label="Less Rework" color="purple" /></Reveal>
            <Reveal delay={0.2}><ValueCard icon={Clock} value="90%+" label="On-Time" color="cyan" /></Reveal>
            <Reveal delay={0.3}><ValueCard icon={Shield} value="<2%" label="Error Rate" color="purple" /></Reveal>
          </div>

          {/* Visual ROI chart */}
          <Reveal delay={0.4}>
            <div className="p-8 rounded-2xl" style={{
              background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.05), rgba(139, 92, 246, 0.05))',
              border: '1px solid rgba(0, 212, 255, 0.1)',
            }}>
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <h3 className="text-xl font-semibold mb-4">Time Savings Per 500 RFQs/Year</h3>
                  <div className="space-y-3">
                    {[
                      { label: 'Hours Saved', value: '2,000-3,000', color: 'cyan' },
                      { label: 'FTE Capacity Released', value: '1-1.5', color: 'purple' },
                      { label: 'Hours Per RFQ Saved', value: '4-6', color: 'cyan' },
                    ].map((stat, i) => (
                      <div key={i} className="flex justify-between items-center p-3 rounded-lg bg-white/5">
                        <span className="text-white/60 text-sm">{stat.label}</span>
                        <span className={`font-bold ${stat.color === 'cyan' ? 'text-cyan-400' : 'text-purple-400'}`}>{stat.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="h-48">
                  <MiniChart type="bar" />
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===== Trusted By: Customer Logos ===== */}
      <section className="py-20 border-t border-white/5" data-testid="customer-logos-section">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <div className="text-center mb-12">
              <span className="text-[#00D4FF] text-xs font-semibold tracking-[0.2em] uppercase">Trusted By Industry Leaders</span>
              <h2 className="text-2xl md:text-3xl font-bold mt-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                Powering <span className="text-gradient-cyan">50+</span> Manufacturers
              </h2>
            </div>
          </Reveal>
          {/* Scrolling marquee */}
          <div className="relative overflow-hidden">
            <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-[#030810] to-transparent z-10 pointer-events-none" />
            <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-[#030810] to-transparent z-10 pointer-events-none" />
            <motion.div
              animate={{ x: ['0%', '-50%'] }}
              transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
              className="flex gap-8 whitespace-nowrap"
            >
              {[...CUSTOMER_LOGOS, ...CUSTOMER_LOGOS].map((customer, i) => (
                <div key={i} className="flex-shrink-0 px-6 py-4 rounded-2xl bg-white border border-white/5 flex items-center justify-center min-w-[180px] h-[92px]"
                  data-testid={`customer-logo-${i}`}>
                  <img
                    src={customer.logo}
                    alt={customer.name}
                    className="max-h-14 max-w-[150px] w-auto object-contain  transition-all duration-300 hover:opacity-100 hover:grayscale-0"
                    loading="lazy"
                  />
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== CTA: Start Now ===== */}
      <section className="py-24" data-testid="cta-section">
        <div className="max-w-3xl mx-auto px-6">
          <Reveal>
            <motion.div
              className="relative rounded-3xl p-12 text-center overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(0, 212, 255, 0.1))',
                border: '1px solid rgba(139, 92, 246, 0.2)',
              }}
            >
              <motion.div
                animate={{ opacity: [0.3, 0.6, 0.3], scale: [1, 1.2, 1] }}
                transition={{ duration: 4, repeat: Infinity }}
                className="absolute top-0 left-1/2 -translate-x-1/2 w-[300px] h-[150px] bg-gradient-to-b from-purple-500/30 to-cyan-500/10 rounded-full blur-[60px]"
              />

              <div className="relative z-10">
                <motion.div
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 3, repeat: Infinity }}
                  className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-purple-500/20 to-cyan-500/20 border border-purple-500/30 flex items-center justify-center"
                >
                  <ArrowRight className="w-8 h-8 text-[#00D4FF]" />
                </motion.div>
                <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Ready to <span className="text-gradient-cyan">Transform</span>?
                </h2>
                <p className="text-white/40 text-sm mb-8">
                  30-day free trial • No credit card • Full access
                </p>

                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  {/* <Link
                    to={REGISTER_URL}
                    className="px-8 py-4 bg-gradient-to-r from-purple-500 to-cyan-500 text-white font-bold text-lg rounded-xl hover:shadow-[0_0_50px_rgba(139,92,246,0.5)] transition-all flex items-center gap-2"
                    data-testid="cta-btn"
                  >
                    <span>Start Free Trial</span>
                    <ArrowRight className="w-5 h-5" />
                  </Link> */}
                  {/* <Link to="/pricing" className="px-8 py-4 text-white/80 font-medium rounded-xl border border-white/20 hover:bg-white/5 transition-all">
                    View Pricing
                  </Link> */}
                </div>
              </div>
            </motion.div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
